import React from 'react';
import './MarketingPopupPreview.css';

function previewReturningFootnote(config) {
  const min = parseFloat(config?.returning_min_order_amount) || 0;
  if (min <= 0) return null;
  const tpl = String(config?.returning_min_order_hint || 'Bij bestelling vanaf € {min}').trim();
  const formatted = min.toFixed(2).replace('.', ',');
  return tpl.replace(/\{min\}/g, formatted);
}

/** Zelfde prioriteit als live: mandje → configurator → views (preview kiest scenario handmatig). */
export function resolveSuccessPreviewCopy(config = {}, variant = 'welcome') {
  if (variant === 'returning_offer') {
    return {
      title: config.success_title_returning || config.success_title || 'Bedankt',
      body:
        config.success_body_applied_returning ||
        config.success_body_returning ||
        config.success_body_applied ||
        config.success_body ||
        '',
    };
  }
  return {
    title: config.success_title || 'Bedankt — je hoort van ons.',
    body: config.success_body_applied || config.success_body || '',
  };
}

export function resolveMarketingPopupPreviewCopy(config = {}, variant = 'welcome', behaviorSegment = null) {
  const isWelcome = variant === 'welcome';
  const isReturningOffer = variant === 'returning_offer';
  const isNewsletter = variant === 'newsletter';

  let title = config.title_welcome;
  let body = config.body_welcome;
  let button = config.button_welcome;
  let pct = config.welcome_percent;
  let effectiveReturning = isReturningOffer;

  if (isNewsletter) {
    title = config.title_newsletter;
    body = config.body_newsletter;
    button = config.button_newsletter;
    pct = null;
  } else if (isReturningOffer) {
    title = config.title_returning_offer;
    body = config.body_returning_offer;
    button = config.button_returning_offer;
    const fixed = parseFloat(config.returning_coupon_fixed_eur) || 0;
    pct = fixed > 0 ? null : config.returning_coupon_percent;
  }

  if (behaviorSegment && config.behavior_enabled !== false) {
    if (behaviorSegment === 'cart_abandon') {
      title = config.title_behavior_cart || title;
      body = config.body_behavior_cart || body;
      button = config.button_behavior_cart || button;
      if (
        (config.returning_coupon_percent || 0) > 0 &&
        config.behavior_cart_use_returning_code &&
        (isReturningOffer || isWelcome)
      ) {
        effectiveReturning = isReturningOffer || Boolean(config.behavior_cart_use_returning_code);
        if (isReturningOffer || (isWelcome && config.behavior_cart_use_returning_code)) {
          pct = config.returning_coupon_percent || pct;
          if (isWelcome && config.behavior_cart_use_returning_code && (config.returning_coupon_percent || 0) > 0) {
            effectiveReturning = true;
          }
        }
      }
    } else if (behaviorSegment === 'high_intent') {
      title = config.title_behavior_intent || title;
      body = config.body_behavior_intent || body;
      button = config.button_behavior_intent || button;
    } else if (behaviorSegment === 'configurator') {
      title = config.title_behavior_configurator || title;
      body = config.body_behavior_configurator || body;
      button = config.button_behavior_configurator || button;
    }
  }

  const fixedEur =
    effectiveReturning && (parseFloat(config.returning_coupon_fixed_eur) || 0) > 0
      ? parseFloat(config.returning_coupon_fixed_eur)
      : null;
  const hasPercentOffer = (isWelcome || effectiveReturning) && !isNewsletter;
  if (isNewsletter) pct = null;

  return {
    title,
    body,
    button,
    pct,
    fixedEur,
    isWelcome: isWelcome && !effectiveReturning,
    isReturningOffer: effectiveReturning,
    isNewsletter,
    hasPercentOffer: hasPercentOffer && pct != null && pct > 0,
    hasFixedOffer: Boolean(fixedEur),
    hasDiscountOffer: (hasPercentOffer && pct > 0) || Boolean(fixedEur),
    bodyFootnote:
      (isReturningOffer || effectiveReturning) && !isNewsletter ? previewReturningFootnote(config) : null,
    offerKicker:
      effectiveReturning && !isNewsletter
        ? String(config?.returning_offer_kicker ?? '').trim()
        : isWelcome
          ? String(config?.welcome_offer_kicker || 'Welkomstkorting').trim()
          : '',
    offerSub:
      effectiveReturning && !isNewsletter
        ? String(config?.returning_offer_sub || 'Eén klik — korting direct in je winkelmand').trim()
        : isWelcome
          ? String(config?.welcome_offer_sub || 'Op je eerste bestelling · code direct in je inbox').trim()
          : '',
    formCompact: effectiveReturning && !isNewsletter,
    knownLabel: String(config?.returning_known_label || 'Korting voor').trim(),
  };
}

/**
 * Visuele preview — spiegelt de modal op 4shine (inkoop-tracking plugin).
 */
export default function MarketingPopupPreview({
  device = 'desktop',
  variant = 'welcome',
  phase = 'form',
  config = {},
  behaviorSegment = null,
  successCode = 'WELKOM-A1B2C3',
  codeIsExample = false,
}) {
  const copy = resolveMarketingPopupPreviewCopy(config, variant, behaviorSegment);
  const {
    title,
    body,
    bodyFootnote,
    button,
    pct,
    fixedEur,
    isWelcome,
    isReturningOffer,
    isNewsletter,
    hasDiscountOffer,
    hasPercentOffer,
    offerKicker,
    offerSub,
    formCompact,
    knownLabel,
  } = copy;
  const successCopy = resolveSuccessPreviewCopy(config, variant);
  const showCodeOnSuccess =
    hasDiscountOffer ||
    (variant === 'returning_offer' && (parseFloat(config.returning_coupon_fixed_eur) || 0) > 0);

  const consent = config.consent_text || '';
  const nameLabel = config.name_label || 'Je naam';
  const namePh = config.name_placeholder || 'Voornaam en achternaam';
  const deviceClass = device === 'mobile' ? 'mpp-device--mobile' : 'mpp-device--desktop';

  return (
    <div className={`mpp-stage ${deviceClass}`}>
      <div className={`mpp-device ${deviceClass}`} aria-hidden>
        <div className="mpp-device-chrome">
          <span className="mpp-device-dot mpp-device-dot--close" />
          <span className="mpp-device-dot mpp-device-dot--min" />
          <span className="mpp-device-dot mpp-device-dot--max" />
          <span className="mpp-device-url">4shine.nl</span>
        </div>
        <div className="mpp-device-screen">
          <div className="mpp-fake-shop">
            <div className="mpp-fake-brand">4SHINE</div>
            <div className="mpp-fake-nav" />
            <div className="mpp-fake-hero">
              <span className="mpp-fake-hero-tag">Haarverzorging</span>
            </div>
            <div className="mpp-fake-grid">
              <div />
              <div />
              <div />
            </div>
          </div>
          <div className="mpp-overlay">
            <div className="mpp-card" role="presentation">
              <div className="mpp-card-accent" aria-hidden />
              <button type="button" className="mpp-close" tabIndex={-1} aria-hidden>
                ×
              </button>
              <div className="mpp-brand-row">
                <span className="mpp-brand-mark">4SHINE</span>
                <span className="mpp-brand-tag">Exclusief voor jou</span>
              </div>

              {phase === 'form' ? (
                <>
                  {hasDiscountOffer ? (
                    <div className="mpp-offer-row">
                      <div className="mpp-percent-badge" aria-hidden>
                        <span className="mpp-percent-num">
                          {fixedEur ? String(fixedEur).replace('.', ',') : pct}
                        </span>
                        <span className="mpp-percent-sym">{fixedEur ? '€' : '%'}</span>
                      </div>
                      <div className="mpp-offer-copy">
                        {offerKicker ? <span className="mpp-kicker">{offerKicker}</span> : null}
                        <p className="mpp-offer-sub">{offerSub || '—'}</p>
                      </div>
                    </div>
                  ) : (
                    <span className="mpp-kicker mpp-kicker--solo">Blijf op de hoogte</span>
                  )}
                  <h2 className="mpp-title">{title || '—'}</h2>
                  <p className="mpp-body">{body || '—'}</p>
                  {bodyFootnote ? <p className="mpp-body-footnote">{bodyFootnote}</p> : null}
                  {formCompact ? (
                    <div className="mpp-known">
                      <span>{knownLabel} </span>
                      <strong>klant@voorbeeld.nl</strong>
                      <span className="mpp-known-change">Ander e-mailadres?</span>
                    </div>
                  ) : (
                    <>
                      <div className="mpp-field mpp-field-fake mpp-field-fake--name" title={nameLabel}>
                        {namePh}
                      </div>
                      <div className={`mpp-field mpp-field-fake ${!isWelcome ? 'mpp-field-prefill' : ''}`}>
                        {!isWelcome ? 'klant@voorbeeld.nl' : 'je@voorbeeld.nl'}
                      </div>
                      {!isWelcome ? (
                        <p className="mpp-email-hint">Herkennen we van je eerdere bestelling (zelfde browser)</p>
                      ) : null}
                    </>
                  )}
                  <label className="mpp-consent">
                    <span className="mpp-checkbox-fake" />
                    <span>{consent || '—'}</span>
                  </label>
                  <div className="mpp-submit-fake">{button || 'Inschrijven'}</div>
                  <p className="mpp-trust">Geen spam · uitschrijven kan altijd · AVG-proof</p>
                </>
              ) : (
                <div className="mpp-success">
                  <div className="mpp-success-icon" aria-hidden>
                    <svg viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="11" stroke="currentColor" strokeWidth="1.5" />
                      <path
                        d="M7 12.5l3 3 7-7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <p className="mpp-success-lead">{successCopy.title}</p>
                  <p className="mpp-success-body">{successCopy.body}</p>
                  {showCodeOnSuccess && successCode ? (
                    <div className="mpp-code-pill">
                      <span className="mpp-code-label">
                        {config.success_code_label || 'Jouw code'}
                        {codeIsExample ? ' (voorbeeld)' : ''}
                      </span>
                      <strong>{successCode}</strong>
                      <button type="button" className="mpp-code-copy-btn" disabled aria-hidden>
                        {config.success_copy_button || 'Kopieer code'}
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
